import { Component, inject, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NotesWithMentionsDto } from '../../../models/notes.models';
import { EntityNotesComponent } from '../entity-notes/entity-notes.component';

export interface TimeOffNotesDialogData {
  timeOffRequestId: number;
  notesInfo?: NotesWithMentionsDto | null;
  dialogTitle?: string;
  employeeName?: string;
  apiMode?: 'admin' | 'self';
  pendingMode?: boolean;
  readOnly?: boolean;
}

@Component({
  selector: 'app-time-off-notes-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    EntityNotesComponent,
  ],
  template: `
    <div class="time-off-notes-dialog">
      <div class="dialog-header">
        <div class="dialog-title-row">
          <h2 class="dialog-title">{{ dialogTitle }}</h2>
        </div>
        <span class="spacer"></span>
        <button mat-icon-button disableRipple (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <mat-dialog-content class="dialog-content">
        <div class="notes-wrapper">
          <app-entity-notes
            #entityNotes
            [entityId]="entityId"
            entityType="timeoff"
            [apiMode]="apiMode"
            [pendingMode]="pendingMode"
            [notesInfo]="notesInfo"
            [title]="employeeName"
            [isReadOnly]="isReadOnly"
            placeholder="Click Edit to add notes..."
            (editModeRequested)="onEditModeRequested()"
            (notesSaved)="onNotesSaved($event)"
            (mentionNavigated)="onMentionNavigated()"
          ></app-entity-notes>
        </div>
      </mat-dialog-content>
    </div>
  `,
  styleUrls: ['./time-off-notes-dialog.component.scss'],
})
export class TimeOffNotesDialogComponent {
  private dialogRef = inject<MatDialogRef<TimeOffNotesDialogComponent>>(MatDialogRef);
  data = inject<TimeOffNotesDialogData | null>(MAT_DIALOG_DATA, { optional: true });

  readonly entityNotes = viewChild.required<EntityNotesComponent>('entityNotes');

  notesInfo: NotesWithMentionsDto | null = null;
  entityId: string;
  dialogTitle: string;
  employeeName: string;
  apiMode: 'admin' | 'self' = 'admin';
  pendingMode: boolean = false;
  isReadOnly: boolean = false;
  private updatedNotesInfo: NotesWithMentionsDto | null = null;

  constructor() {
    const data = this.data;

    this.entityId = data?.timeOffRequestId?.toString() ?? '0';
    this.dialogTitle = data?.dialogTitle ?? 'Time Off Notes';
    this.employeeName = data?.employeeName ?? '';
    this.notesInfo = data?.notesInfo ?? null;
    this.apiMode = data?.apiMode ?? 'admin';
    this.pendingMode = data?.pendingMode ?? false;
    this.isReadOnly = data?.readOnly ?? false;
  }

  onEditModeRequested(): void {
    this.entityNotes().enterEditMode();
  }

  onNotesSaved(notesWithMentions: NotesWithMentionsDto): void {
    this.updatedNotesInfo = notesWithMentions;
    if (this.pendingMode) {
      // In pending mode, close immediately with the raw notes content
      this.dialogRef.close(this.updatedNotesInfo);
    }
  }

  onMentionNavigated(): void {
    this.dialogRef.close(this.updatedNotesInfo);
  }

  close(): void {
    const entityNotes = this.entityNotes();
    if (entityNotes?.isEditMode) {
      entityNotes.tryExitEditMode().then((exited) => {
        if (exited) {
          this.dialogRef.close(this.updatedNotesInfo);
        }
      });
    } else {
      this.dialogRef.close(this.updatedNotesInfo);
    }
  }
}
